import React, { useEffect, useState } from 'react';
import { ApplicationView } from '@aha-app/mvc';
import styles from './ConversationPanel.module.css';
import { type MessageProps } from './MessageContent';

const HtmlContent = ({ body }: MessageProps) => {
  // We use a random subdomain for each app to avoid any cookies from being
  // shared between apps.
  const [targetOrigin, setTargetOrigin] = React.useState<string | null>(null);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [debouncedBody, setDebouncedBody] = useState<string | undefined>(body);
  const [handler, setHandler] = useState<
    string | number | NodeJS.Timeout | null
  >(null);

  useEffect(() => {
    const randomSubdomain = Math.random()
      .toString(36)
      .substring(2, 10 + 2);

    setTargetOrigin(
      `https://${randomSubdomain}.${window.Configatron?.userContentDomain}`
    );
  }, []);

  // Debounce the body to avoid spam reloading the iframe cause it looks ugly.
  useEffect(() => {
    if (!handler) {
      const handler = setTimeout(() => {
        setDebouncedBody(body);
        setHandler(null);
      }, 500);
      setHandler(handler);
    }
  }, [body, handler]);

  useEffect(() => {
    if (iframeRef.current && debouncedBody && targetOrigin && iframeLoaded) {
      iframeRef.current.contentWindow?.postMessage(
        {
          body: debouncedBody,
        },
        targetOrigin
      );
    }
  }, [debouncedBody, targetOrigin, iframeLoaded]);

  if (!targetOrigin) {
    return null;
  }

  if (!window.currentAccount?.aiAllowScriptableArtifacts) {
    return (
      <aha-empty-state>
        <h2 slot='title'>Content type disabled</h2>
        Scriptable content types are disabled for your account. Please contact
        your account administrator.
      </aha-empty-state>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      className={styles.htmlContent}
      onLoad={e => {
        e.currentTarget.removeAttribute('srcdoc');
        e.currentTarget.contentWindow?.postMessage(
          { body: debouncedBody },
          targetOrigin
        );
        setIframeLoaded(true);
      }}
      src={`${targetOrigin}/assistant/sandbox?acceptOrigin=${encodeURIComponent(
        window.location.origin
      )}`}
    />
  );
};

export default ApplicationView(HtmlContent);
